import { HttpService } from "../../../utils/DesigningHttpService.js";
import { readStore } from "../storefrontService.ts";
import { canWriteAgencyCommerce, getAgencySession, canChangeOrderStatus, isCompanySalesAgent } from "../../../utils/agencySession.ts";
import { t } from "../../../features/store-language/i18n.ts";

export interface AccountOrderLine {
  id: number;
  productVariantId: number;
  title: string;
  code: string;
  quantity: number;
  sourceUnitPrice: number | null;
  area: number | null;
  reedCountTitle: string | null;
  paletteTitle: string | null;
  thumbnailMediaId?: number;
  fileId?: string;
}

export interface AccountOrder {
  salesAgencyId: number;
  agencyTitle: string;
  id: number;
  version: string;
  orderNumber: string;
  orderedAt: string;
  status: string;
  title?: string | null;
  description: string | null;
  cancellationReason?: string | null;
  placedByDisplayName?: string | null;
  itemCount: number;
  items: AccountOrderLine[];
  attachments: AccountOrderAttachment[];
}

export interface AccountOrderAttachment {
  id: number;
  title: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
}

export interface AccountOrderAttachmentUpload {
  fileId: number;
  fileName: string;
  fileSize: number;
  mimeType: string;
}

export interface AccountOrderDraft {
  salesAgencyId?: number;
  title?: string;
  description: string;
  items: AccountOrderLine[];
}

export function accountOrderStatusLabel(status: string): string {
  if (status === "Draft") return t("پیش نویس");
  if (status === "Confirmed") return t("قطعی");
  if (status === "Received") return t("دریافت‌شده");
  if (status === "Approved") return t("تأییدشده");
  if (status === "Cancelled") return t("لغوشده");
  return status;
}

type PendingOrder = { requestId: string; draft: AccountOrderDraft };
const http = new HttpService();
const productSearchHttp = new HttpService();

function pendingKey() {
  const session = getAgencySession();
  return `account-order:${window.location.host}:${session?.userId}:${session?.salesAgencyId}:${session?.membershipId}`;
}

export function pendingAccountOrder(): PendingOrder | undefined {
  try {
    const saved = sessionStorage.getItem(pendingKey());
    if (!saved) return undefined;
    const pending: PendingOrder = JSON.parse(saved);
    return typeof pending.requestId === "string" && Array.isArray(pending.draft?.items) ? pending : undefined;
  }
  catch { return undefined; }
}

function requireWrite() {
  if (!canWriteAgencyCommerce()) throw new Error(t("اجازهٔ تغییر سفارش را ندارید."));
}

export async function readAccountOrders(pageSize: number) {
  return readStore(`orders?page=1&pageSize=${pageSize}`) as Promise<{ items: AccountOrder[]; total: number }>;
}

export async function readAccountOrder(id: number): Promise<AccountOrder> {
  const order: AccountOrder = await readStore(`orders/${id}`);
  return { ...order, items: order.items.map(function (line) {
    return { ...line, fileId: line.thumbnailMediaId ? `store-order:${id}:${line.thumbnailMediaId}` : undefined };
  }) };
}

export async function findAccountOrderProducts(search: string, signal?: AbortSignal): Promise<AccountOrderLine[]> {
  const variants: { id: number; title: string }[] = await productSearchHttp.getResource(`/catalog/variants?search=${encodeURIComponent(search)}`, signal);
  return variants.map(function (variant) {
    return { id: -variant.id, productVariantId: variant.id, title: variant.title, code: "",
      quantity: 1, sourceUnitPrice: null, area: null, reedCountTitle: null, paletteTitle: null };
  });
}

function lines(draft: AccountOrderDraft) {
  return draft.items.map(function (line) {
    return { id: line.id > 0 ? line.id : null, productVariantId: line.productVariantId, quantity: line.quantity };
  });
}

export async function createAccountOrder(draft: AccountOrderDraft): Promise<{ orderId: number }> {
  requireWrite();
  const key = pendingKey();
  const pending = pendingAccountOrder() || { requestId: crypto.randomUUID(), draft };
  if (isCompanySalesAgent() && !pending.draft.salesAgencyId) throw new Error(t("نمایندگی را انتخاب کنید."));
  // Retain the exact request across network failures, reloads and explicit retries.
  sessionStorage.setItem(key, JSON.stringify(pending));
  try {
    const result = await http.postasync("/catalog/orders", {
      salesAgencyId: pending.draft.salesAgencyId, requestId: pending.requestId, title: pending.draft.title, description: pending.draft.description, items: lines(pending.draft),
    });
    sessionStorage.removeItem(key);
    return result.data;
  } catch (error) {
    if ((error as { response?: { status?: number } }).response?.status === 400) sessionStorage.removeItem(key);
    throw error;
  }
}

export async function editAccountOrder(order: AccountOrder, draft: AccountOrderDraft) {
  requireWrite();
  const result: AccountOrder = await http.putForData(`/catalog/orders/${order.id}`, {
    version: order.version, salesAgencyId: draft.salesAgencyId, title: draft.title, description: draft.description, items: lines(draft),
  });
  return { ...result, items: result.items.map(function (line) {
    return { ...line, fileId: line.thumbnailMediaId ? `store-order:${result.id}:${line.thumbnailMediaId}` : undefined };
  }) };
}

export async function deleteAccountOrder(order: AccountOrder) {
  requireWrite();
  if (order.status !== "Draft") throw new Error(t("فقط سفارش پیش‌نویس قابل حذف است."));
  await http.postasync(`/catalog/orders/${order.id}/delete`, { version: order.version });
}

export async function changeAccountOrderStatus(order: AccountOrder, status: AccountOrder["status"], cancellationReason?: string) {
  requireWrite();
  if (!canChangeOrderStatus()) throw new Error(t("فقط مدیر نمایندگی می‌تواند وضعیت سفارش را تغییر دهد."));
  await http.putForData(`/catalog/orders/${order.id}/status`, {
    version: order.version, status, cancellationReason,
  });
}

export async function uploadAccountOrderAttachmentFile(orderId: number, file: File): Promise<AccountOrderAttachmentUpload> {
  requireWrite();
  const form = new FormData();
  form.append("file", file, file.name);
  const response = await http.postasync(`/catalog/orders/${orderId}/attachment-files`, form);
  return response.data;
}

export async function attachAccountOrderFile(orderId: number, fileId: number): Promise<void> {
  requireWrite();
  await http.postasync(`/catalog/orders/${orderId}/attachments`, { mediaAssetId: fileId });
}

export async function discardAccountOrderAttachmentFile(orderId: number, fileId: number): Promise<void> {
  requireWrite();
  await http.postasync(`/catalog/orders/${orderId}/attachment-files/${fileId}/delete`, {});
}

export async function removeAccountOrderAttachment(orderId: number, attachmentId: number): Promise<void> {
  requireWrite();
  await http.postasync(`/catalog/orders/${orderId}/attachments/${attachmentId}/delete`, {});
}
